import { type UseQueryResult, useQuery } from "@tanstack/react-query";
import { getBeneProvidersMapnet } from "../../../api";
import { StaleTime } from "../types";
import type {
  BeneProvider,
  BeneProviderCredential,
  BeneProviderSpecialty,
  MctProvider,
  PractitionerCredential,
  PractitionerSpecialty,
} from "../../../@types";
import { useLoggedIn } from "../../loginHelpers";
import { useBeneinfoProvidersMapnetQueryKey } from "../queryKeyHooks";
import { useMemo } from "react";
import { useMapnetCoverage } from "../../flag-hooks/useMapnetCoverage";
import { useAppContext } from "../../context-hooks/useAppContext";

const toPractitionerCredential = (
  credential: BeneProviderCredential
): PractitionerCredential => ({
  credential_code: credential.code,
  credential_display_name: credential.name,
});

const toPractitionerSpecialty = (
  specialty: BeneProviderSpecialty
): PractitionerSpecialty => ({
  specialty_code: specialty.code,
  specialty_display_name: specialty.name,
});

const toMctProvider = (provider: BeneProvider): MctProvider => ({
  npi: provider.npi,
  firstName: provider.first_name,
  lastName: provider.last_name,
  searchCenter: { latitude: provider.latitude, longitude: provider.longitude },
  credentials: provider.credentials
    .map(credential => credential.code)
    .join(", "),
  specialties: provider.specialties.map(specialty => specialty.name).join(", "),
  credentialsArray: provider.credentials.map(credential =>
    toPractitionerCredential(credential)
  ),
  specialtiesArray: provider.specialties.map(specialty =>
    toPractitionerSpecialty(specialty)
  ),
  total_locations: provider.total_locations,
  returned_locations: provider.returned_locations,
  total_covered_locations: provider.total_covered_locations,
  covered_returned_locations: provider.covered_returned_locations,
  name: `${provider.last_name}, ${provider.first_name}`,
  locations: provider.locations.map(location => {
    const address = [
      location.address_line_1,
      location.address_line_2,
      location.city,
      location.state,
      location.zip_code,
    ]
      .filter(Boolean)
      .join(", ");

    return {
      id: address,
      address,
    };
  }),
});

/**
 * Fetch a logged-in bene's saved providers when Mapnet is enabled
 * */
export const useGetBeneProvidersMapnet = (): Omit<
  Partial<UseQueryResult<MctProvider[], Error>>,
  "data"
> & {
  providers: MctProvider[];
} => {
  const { state } = useAppContext();
  const { enableMapnetCoverage } = useMapnetCoverage(state.year);
  const beneinfoProvidersMapnetQueryKey = useBeneinfoProvidersMapnetQueryKey();
  const isLoggedIn = useLoggedIn();

  // * Queries
  // Map to MctProvider in queryFn so the cache always holds
  // MctProvider[] -- matching the shape optimistic updates write via
  // `queryClient.setQueryData` in useProviders.
  const { data, error, isError, isLoading } = useQuery({
    enabled: enableMapnetCoverage && isLoggedIn,
    queryKey: beneinfoProvidersMapnetQueryKey,
    queryFn: async () => {
      const beneProviders = await getBeneProvidersMapnet();
      return beneProviders.map(provider => toMctProvider(provider));
    },
    staleTime: StaleTime.Infinite,
  });

  const providers = useMemo(() => data ?? [], [data]);

  return {
    error,
    isError,
    isLoading,
    providers,
  };
};
